import { mediaPreview } from '../lib/mediaDelivery';
import { getTrafficSource } from '../lib/trafficSource';
import React, { useContext, useEffect, useId, useRef, useState, lazy, Suspense } from 'react';
import { FiEye, FiDownload, FiShare2 } from 'react-icons/fi';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { postPath } from '../lib/postLinks';
import ShareDialog from './ShareDialog';
import { playShareSound } from '../lib/shareSound';
import { API_URL } from '../config/api';
import { UserContext } from '../App';
import { useFeedMuted, setFeedMuted, markPostSeen } from '../lib/feedPreferences';
import CommentModalController from './CommentModalController';

const PostAnalyticsDialog = lazy(() => import('./admin/PostAnalyticsDialog'));

export default function Tv({ file_link: coverLink, file_type: coverType, media: postMedia, post_id, caption, priority = false, preferVideo = false, numberOfComments = 0, views = 0, unique_view_count, stored_view_count }) {
  const user = useContext(UserContext);
  const isViewAdmin = user?.email === 'warigiachegesa@gmail.com';
  const [showInsights, setShowInsights] = useState(false);
  const downloadTarget = `meme-download-${useId().replace(/:/g, '')}`;
  const slides = Array.isArray(postMedia) && postMedia.length ? postMedia.slice(0, 5) : [{ file_link: coverLink, file_type: coverType }];
  const firstSlide = preferVideo ? Math.max(0, slides.findIndex(item => /video/i.test(item.file_type || '') || /\.(mp4|webm|mov)(\?|$)/i.test(item.file_link || ''))) : 0;
  const [slideIndex, setSlideIndex] = useState(firstSlide);
  const touch = useRef(null);
  const mouseDrag = useRef(null);
  const activeIndex = Math.min(slideIndex, slides.length - 1);
  const { file_link, file_type } = slides[activeIndex];
  useEffect(() => { setSlideIndex(firstSlide); }, [post_id, firstSlide]);
  const fetchedViews = Number(views);
  const displayedViews = Number.isFinite(fetchedViews) ? Math.max(0, fetchedViews) : 0;
  const muted = useFeedMuted();
  const autoplayMuted = useRef(false);
  const media = useRef(null);
  const viewed = useRef(false);
  const video = useRef(null);
  const backdrop = useRef(null);
  const [failed, setFailed] = useState(false);
  const [originalImage, setOriginalImage] = useState(false);
  useEffect(() => { setOriginalImage(false); }, [post_id, file_link]);
  const [retry, setRetry] = useState(0);
  const [readyKey, setReadyKey] = useState(null);
  const [bufferingKey, setBufferingKey] = useState(null);
  const [frameRatio, setFrameRatio] = useState(null);
  const mediaKey = `${post_id}-${activeIndex}-${retry}-${file_link}`;
  const mediaLoading = Boolean(file_link) && !failed && (readyKey !== mediaKey || bufferingKey === mediaKey);
  useEffect(() => { setFrameRatio(null); }, [post_id, file_link]);
  const mediaReady = element => {
    const width = element.naturalWidth || element.videoWidth;
    const height = element.naturalHeight || element.videoHeight;
    if (width && height) setFrameRatio(width / height);
    setReadyKey(mediaKey); setBufferingKey(null);
  };
  const [shareLink, setShareLink] = useState('');
  const type = String(file_type || '').toLowerCase();
  const isVideo = type.includes('video') || /\.(mp4|webm|mov)(\?|$)/i.test(file_link || '');

  useEffect(() => {
    viewed.current = false;
    setFailed(false);
    if (!media.current || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || viewed.current) return;
      viewed.current = true;
      fetch(`${API_URL}/api/v1/posts/views`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ post_id, user_id: user?.email || null, traffic_source: getTrafficSource().source }),
      }).catch(() => {});
      observer.disconnect();
    }, { threshold: 0.15 });
    observer.observe(media.current);
    return () => observer.disconnect();
  }, [post_id, user?.email]);

  useEffect(() => {
    if (!isVideo || !video.current) return;
    const players = [video.current, backdrop.current].filter(Boolean);
    let visible = false;
    const update = () => players.forEach(player => {
      if (visible && !document.hidden) {
        const promise = player.play();
        if (promise) promise.catch(() => {
          // A browser can require a new gesture before autoplay with sound.
          if (player === video.current && !player.muted) {
            autoplayMuted.current = true;
            player.muted = true;
            const fallback = player.play();
            if (fallback) fallback.catch(() => {});
          }
        });
      } else player.pause();
    });
    const observer = window.IntersectionObserver ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      update();
    }, { threshold: [0, 0.5] }) : null;
    if (observer) observer.observe(video.current);
    else { visible = true; update(); }
    document.addEventListener('visibilitychange', update);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', update);
      players.forEach(player => player.pause());
    };
  }, [isVideo, file_link, retry, failed]);

  useEffect(() => {
    if (!media.current || !window.IntersectionObserver || failed || !file_link || mediaLoading) return;
    let timer;
    const observer = new IntersectionObserver(([entry]) => {
      clearTimeout(timer);
      if (entry.isIntersecting && entry.intersectionRatio >= 0.15 && !document.hidden) {
        timer = setTimeout(() => {
          const openViewer = document.querySelector('.post-status-viewer');
          if (!document.hidden && (!openViewer || media.current?.closest('.post-status-viewer'))) markPostSeen(post_id);
        }, 1500);
      }
    }, { threshold: [0, 0.15] });
    observer.observe(media.current);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [post_id, failed, file_link, mediaLoading]);

  const selectSlide = index => {
    if (index < 0 || index >= slides.length || index === activeIndex) return;
    video.current?.pause(); backdrop.current?.pause();
    autoplayMuted.current = false; setFailed(false); setSlideIndex(index);
  };
  const canSwipe = (target, y) => {
    if (slides.length < 2 || target.closest('button, a')) return false;
    // Leave the native video's bottom control strip available for seeking.
    return target.tagName !== 'VIDEO' || y < target.getBoundingClientRect().bottom - 48;
  };
  const finishSwipe = (start, x, y) => {
    if (!start) return;
    const dx = x - start.x, dy = y - start.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) selectSlide(activeIndex + (dx < 0 ? 1 : -1));
  };
  const share = () => {
    if (!shareLink) playShareSound();
    setShareLink(value => value ? '' : `${window.location.origin}${postPath(post_id)}`);
  };

  return <div className="Tv-1">
    <div className="post-carousel" role={slides.length > 1 ? 'region' : undefined} aria-roledescription={slides.length > 1 ? 'carousel' : undefined} aria-label={slides.length > 1 ? 'Post media' : undefined} tabIndex={slides.length > 1 ? 0 : undefined}
      onKeyDown={event => { if (event.target !== event.currentTarget) return; if (event.key === 'ArrowRight') { event.preventDefault(); selectSlide(activeIndex + 1); } if (event.key === 'ArrowLeft') { event.preventDefault(); selectSlide(activeIndex - 1); } }}
      onTouchCancel={() => { touch.current = null; }}
      onTouchStart={event => { touch.current = null; if (event.touches.length !== 1) return; const p = event.touches[0]; if (canSwipe(event.target, p.clientY)) touch.current = { x: p.clientX, y: p.clientY }; }}
      onTouchEnd={event => { const start = touch.current; touch.current = null; const p = event.changedTouches[0]; if (p) finishSwipe(start, p.clientX, p.clientY); }}
      onPointerDown={event => { if (event.pointerType !== 'mouse' || event.button !== 0 || !canSwipe(event.target, event.clientY)) return; mouseDrag.current = { x: event.clientX, y: event.clientY }; event.currentTarget.setPointerCapture?.(event.pointerId); }}
      onPointerUp={event => { const start = mouseDrag.current; mouseDrag.current = null; if (event.pointerType === 'mouse') finishSwipe(start, event.clientX, event.clientY); }}
      onPointerCancel={() => { mouseDrag.current = null; }}
      onLostPointerCapture={() => { mouseDrag.current = null; }}>
    <div className="film-frame">
      <div className="film-edge-markings" aria-hidden="true"><span>HAPPY TIMES · 400</span><span>▸ {String(activeIndex + 1).padStart(2, '0')} A</span></div>
      {file_link && !failed && <div className="media-backdrop frame-ambience" aria-hidden="true">
        {isVideo ? <video ref={backdrop} src={file_link} muted loop playsInline preload="none" tabIndex={-1} /> : <img src={mediaPreview(file_link, 96)} alt="" loading="lazy" fetchpriority="low" />}
      </div>}

    <div ref={media} className={`monitor${mediaLoading ? ' is-loading' : ''}`} style={{ aspectRatio: frameRatio || 1 }} aria-busy={mediaLoading}>
      {mediaLoading && <div className="media-loading" role="status"><span className="media-loading-spinner" aria-hidden="true" /><span>Loading media…</span></div>}
      {!file_link || failed ? <div className="media-error" role="status"><p>This media couldn’t load.</p><button onClick={() => { setFailed(false); setRetry(value => value + 1); }}>Retry media</button><a href={file_link} target="_blank" rel="noreferrer">Open original</a></div>
        : isVideo ? <video ref={video} muted={muted} onVolumeChange={event => {
          if (autoplayMuted.current && event.currentTarget.muted) return;
          autoplayMuted.current = false;
          setFeedMuted(event.currentTarget.muted);
        }} loop key={`${post_id}-${activeIndex}-${retry}`} controls playsInline poster={mediaPreview(file_link, 800) !== file_link ? mediaPreview(file_link, 800) : undefined} preload={priority ? 'metadata' : 'none'} onLoadedData={event => mediaReady(event.currentTarget)} onCanPlay={event => mediaReady(event.currentTarget)} onPlaying={event => mediaReady(event.currentTarget)} onWaiting={() => setBufferingKey(mediaKey)} onError={() => setFailed(true)} src={file_link} />
        : <img key={`${post_id}-${activeIndex}-${retry}`} src={originalImage ? file_link : mediaPreview(file_link, 800)} alt="Meme shared by the community" draggable={false} loading={priority ? 'eager' : 'lazy'} fetchpriority={priority ? 'high' : 'auto'} decoding="async" onLoad={event => mediaReady(event.currentTarget)} onError={() => { if (!originalImage && mediaPreview(file_link, 800) !== file_link) setOriginalImage(true); else setFailed(true); }} />} 
    </div>
    {caption && caption !== '1' && <div className="film-caption">{(() => { try { return decodeURIComponent(caption); } catch { return caption; } })()}</div>}
    </div>
    {slides.length > 1 && <div className="carousel-navigation"><button type="button" aria-label="Previous media" disabled={activeIndex === 0} onClick={() => selectSlide(activeIndex - 1)}><BiChevronLeft /></button><div className="carousel-dots">{slides.map((_, index) => <button type="button" key={index} aria-label={`Show item ${index + 1}`} aria-current={index === activeIndex ? 'true' : undefined} onClick={() => selectSlide(index)}><span /></button>)}</div><span className="carousel-count" aria-live="polite">{activeIndex + 1} / {slides.length}</span><button type="button" aria-label="Next media" disabled={activeIndex === slides.length - 1} onClick={() => selectSlide(activeIndex + 1)}><BiChevronRight /></button></div>}
    </div>
    <div className="rxns-container"><div className="post-reactions"><div className="rxns-parent">
      {isViewAdmin ? <button className="views post-insights-trigger" aria-label={`${displayedViews} views — view visitor details`} aria-haspopup="dialog" onClick={() => setShowInsights(true)}><FiEye aria-hidden="true" /><span>{displayedViews}</span></button> : <div className="views" aria-label={`${displayedViews} views`}><FiEye aria-hidden="true" /><p>{displayedViews}</p></div>}
      <CommentModalController post_id={post_id} numberOfComments={numberOfComments} />
      {user ? <a className="media-action" aria-label="Download meme" title="Save with happytimesmemes branding" href={`${API_URL}/downloadFile?fileDetails=${encodeURIComponent(file_link || '')}`} target={downloadTarget}><FiDownload aria-hidden="true" /><span className="action-label">Download</span></a> : <a className="media-action" aria-label="Download meme" title="Sign in to download" href="/account"><FiDownload aria-hidden="true" /><span className="action-label">Download</span></a>}
      <button className="media-action" aria-label="Share meme" aria-expanded={Boolean(shareLink)} aria-controls={shareLink ? `share-${post_id}` : undefined} aria-haspopup="dialog" onClick={share}><FiShare2 aria-hidden="true" /></button>
    </div></div></div>
    {user?.email === 'warigiachegesa@gmail.com' && unique_view_count !== undefined && <div className="post-view-breakdown" aria-label="Post view breakdown"><span>Total <strong>{Number(stored_view_count ?? views) || 0}</strong></span><button className="post-unique-insights" onClick={() => setShowInsights(true)} aria-haspopup="dialog">Unique <strong>{Number(unique_view_count) || 0}</strong> · View visitors</button></div>}
    {user && <iframe name={downloadTarget} title="Meme download" hidden aria-hidden="true" tabIndex={-1} />}
    {isViewAdmin && showInsights && <Suspense fallback={<div role="status">Loading post insights…</div>}><PostAnalyticsDialog postId={post_id} onClose={() => setShowInsights(false)} /></Suspense>}
    {shareLink && <ShareDialog user={user} url={shareLink} postId={post_id} onClose={() => setShareLink('')} />}

  </div>;
}
